// 订单导出逻辑封装

import { useState, useCallback } from 'react';
import { message } from 'antd';
import type { OrderData } from '@/shared/api/types';
import type { ExtendedSearchOrdersParams, ExportFormat, ExportConfig } from '../types';
import { orderApi } from '@/shared/api/orderApi';
import {
  convertOrdersToExportFormat,
  generateExportFileName,
  downloadFile
} from '../utils/orderUtils';

export const useOrderExport = () => {
  const [exporting, setExporting] = useState(false);

  /**
   * 导出订单数据
   */
  const exportOrders = useCallback(async (
    params: ExtendedSearchOrdersParams,
    config: Partial<ExportConfig> = {}
  ) => {
    const {
      format = 'csv',
      includeAllColumns = true,
      selectedColumns = [],
      fileName
    } = config;

    try {
      setExporting(true);
      message.loading({ content: '正在准备导出数据...', key: 'export', duration: 0 });

      // 1. 获取订单数据
      const response = await orderApi.searchOrders(params);
      const orders: OrderData[] = response.data?.orders || [];

      if (orders.length === 0) {
        message.warning('没有可导出的订单数据');
        return;
      }

      // 2. 转换数据格式
      const exportContent = convertOrdersToExportFormat(
        orders,
        format,
        includeAllColumns,
        selectedColumns
      );

      // 3. 确定文件名
      const finalFileName = fileName || generateExportFileName(format);

      // 4. 确定MIME类型
      const mimeTypes: Record<ExportFormat, string> = {
        csv: 'text/csv;charset=utf-8;',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        json: 'application/json'
      };

      // 5. 下载文件
      downloadFile(exportContent, finalFileName, mimeTypes[format]);

      // 6. 显示成功消息
      message.success({
        content: `成功导出 ${orders.length} 条订单数据`,
        key: 'export'
      });

    } catch (error) {
      console.error('订单导出失败:', error);
      message.error({
        content: `导出失败: ${error instanceof Error ? error.message : '未知错误'}`,
        key: 'export'
      });
    } finally {
      setExporting(false);
    }
  }, []);

  /**
   * 导出当前页面数据
   */
  const exportCurrentPage = useCallback(async (
    orders: OrderData[],
    config: Partial<ExportConfig> = {}
  ) => {
    const {
      format = 'csv',
      includeAllColumns = true,
      selectedColumns = [],
      fileName
    } = config;

    try {
      setExporting(true);

      if (orders.length === 0) {
        message.warning('当前页面没有可导出的订单数据');
        return;
      }

      // 转换数据格式
      const exportContent = convertOrdersToExportFormat(
        orders,
        format,
        includeAllColumns,
        selectedColumns
      );

      // 确定文件名
      const finalFileName = fileName || generateExportFileName(format, `orders_page_${new Date().getTime()}`);

      // 确定MIME类型
      const mimeTypes: Record<ExportFormat, string> = {
        csv: 'text/csv;charset=utf-8;',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        json: 'application/json'
      };

      // 下载文件
      downloadFile(exportContent, finalFileName, mimeTypes[format]);

      message.success(`成功导出当前页面 ${orders.length} 条订单数据`);

    } catch (error) {
      console.error('页面数据导出失败:', error);
      message.error(`导出失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setExporting(false);
    }
  }, []);

  /**
   * 导出选中的数据
   */
  const exportSelected = useCallback(async (
    selectedOrders: OrderData[],
    config: Partial<ExportConfig> = {}
  ) => {
    const {
      format = 'csv',
      includeAllColumns = true,
      selectedColumns = [],
      fileName
    } = config;

    try {
      setExporting(true);

      if (selectedOrders.length === 0) {
        message.warning('请先选择要导出的订单');
        return;
      }

      // 转换数据格式
      const exportContent = convertOrdersToExportFormat(
        selectedOrders,
        format,
        includeAllColumns,
        selectedColumns
      );

      // 确定文件名
      const finalFileName = fileName || generateExportFileName(format, `selected_orders_${new Date().getTime()}`);

      // 确定MIME类型
      const mimeTypes: Record<ExportFormat, string> = {
        csv: 'text/csv;charset=utf-8;',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        json: 'application/json'
      };

      // 下载文件
      downloadFile(exportContent, finalFileName, mimeTypes[format]);

      message.success(`成功导出 ${selectedOrders.length} 条选中订单数据`);

    } catch (error) {
      console.error('选中数据导出失败:', error);
      message.error(`导出失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setExporting(false);
    }
  }, []);

  /**
   * 批量导出操作
   */
  const batchExport = useCallback(async (
    selectedOrderIds: string[],
    params: ExtendedSearchOrdersParams,
    config: Partial<ExportConfig> = {}
  ) => {
    if (selectedOrderIds.length === 0) {
      message.warning('请先选择要导出的订单');
      return;
    }

    try {
      setExporting(true);
      message.loading({ content: `正在导出 ${selectedOrderIds.length} 条订单...`, key: 'batch-export' });

      // 如果有选中的ID，使用详情接口获取完整数据
      // 否则使用搜索参数获取数据
      if (selectedOrderIds.length > 0) {
        // 这里可以优化为并行请求或批量接口
        const orders: OrderData[] = [];
        for (const orderId of selectedOrderIds.slice(0, 100)) { // 限制最多100条
          try {
            const response = await orderApi.getOrder(orderId, params.company_id);
            if (response.data?.success && response.data.data) {
              orders.push(response.data.data as OrderData);
            }
          } catch (error) {
            console.error(`获取订单 ${orderId} 详情失败:`, error);
          }
        }

        if (orders.length > 0) {
          await exportSelected(orders, config);
        } else {
          message.warning('没有成功获取到订单数据');
        }
      } else {
        // 使用搜索参数导出
        await exportOrders(params, config);
      }

      message.success({
        content: `批量导出完成`,
        key: 'batch-export'
      });

    } catch (error) {
      console.error('批量导出失败:', error);
      message.error({
        content: `批量导出失败: ${error instanceof Error ? error.message : '未知错误'}`,
        key: 'batch-export'
      });
    } finally {
      setExporting(false);
    }
  }, [exportOrders, exportSelected]);

  return {
    exporting,
    exportOrders,
    exportCurrentPage,
    exportSelected,
    batchExport
  };
};